import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import EmojiPicker from 'emoji-picker-react';
import { createCategory, updateCategory } from '../../redux/knowledgeBaseSlice';
import { toDisplayList, getDescendantIds, indentedLabel } from './categoryTree';
import VisibilityPicker from './VisibilityPicker';
import { EVERYONE, audienceFromCategory, isRestricted, summarize } from './visibilityState';
import { labelStyle } from './kbStyles';

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const slugify = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const CategoryEditorModal = ({ category, categories = [], show, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const isNew = !category;

  const [form, setForm] = useState({
    name: category?.name || '',
    slug: category?.slug || '',
    icon: category?.icon || '',
    sortOrder: category?.sort_order ?? 0,
    parentId: category?.parent_id ?? '',
  });
  // WEB-829 audience. Only meaningful on a top-level category; a sub-category
  // inherits its top-level ancestor's audience and never sends these. null
  // means the row came without audience data, so editing it is disabled
  // rather than defaulting to "Everyone" (access settings fail closed).
  const [audience, setAudience] = useState(() => (category ? audienceFromCategory(category) : EVERYONE));
  const audienceUnavailable = audience === null;
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  // Parent choices: every category except this one and anything nested
  // under it. Picking a descendant would create a cycle; the API rejects
  // that too, this just keeps the option out of the list.
  const parentOptions = useMemo(() => {
    const excluded = new Set(
      category ? [category.category_id, ...getDescendantIds(categories, category.category_id)] : []
    );
    return toDisplayList(categories).filter(c => !excluded.has(c.category_id));
  }, [categories, category]);

  // The select only ever yields strings; '' is "None (top level)".
  const isTopLevel = form.parentId === '';
  const parentName = categories.find(c => String(c.category_id) === String(form.parentId))?.name;
  // Nesting a restricted category makes it inherit the parent's audience; the
  // API clears the old settings, so say so before the admin saves.
  const nestingDropsAudience = !isTopLevel && !audienceUnavailable && isRestricted(audience);

  const handleNameChange = (value) => {
    setForm(f => ({
      ...f,
      name: value,
      slug: slugTouched ? f.slug : slugify(value),
    }));
  };

  const handleSlugChange = (value) => {
    setSlugTouched(true);
    setForm(f => ({ ...f, slug: value }));
  };

  const handleSave = async () => {
    const name = form.name.trim();
    const slug = form.slug.trim();

    if (!name) {
      setError('Name is required.');
      return;
    }
    if (!slug) {
      setError('Slug is required.');
      return;
    }
    if (!SLUG_PATTERN.test(slug)) {
      setError('Slug must be lowercase letters, numbers, and hyphens (e.g. "tax-sales").');
      return;
    }

    const sortOrderNum = parseInt(form.sortOrder, 10);
    if (form.sortOrder !== '' && isNaN(sortOrderNum)) {
      setError('Sort order must be a number.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const data = {
        name,
        slug,
        icon: form.icon.trim() || null,
        sortOrder: isNaN(sortOrderNum) ? 0 : sortOrderNum,
        parentId: isTopLevel ? null : parseInt(form.parentId, 10),
        // Hidden/shown is not editable here; the row's Hide/Show button in
        // AdminTab owns it, and the API leaves is_active alone when omitted.
        // Audience ({ isPublic, appIds, roleIds }) only for a top-level
        // category with editable audience data; the API rejects it otherwise.
        ...(isTopLevel && !audienceUnavailable ? audience : {}),
      };

      if (isNew) {
        await dispatch(createCategory(data)).unwrap();
      } else {
        await dispatch(updateCategory({ categoryId: category.category_id, data })).unwrap();
      }
      onSaved();
    } catch (err) {
      setError(err?.message || 'Failed to save category.');
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '17px' }}>
          {isNew ? 'New Category' : 'Edit Category'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        <Form.Group className="mb-3">
          <Form.Label className="text-muted text-uppercase" style={labelStyle}>
            Name *
          </Form.Label>
          <Form.Control
            value={form.name}
            onChange={e => handleNameChange(e.target.value)}
            placeholder="e.g. Tax Sales"
            autoFocus
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="text-muted text-uppercase" style={labelStyle}>
            Slug *
          </Form.Label>
          <Form.Control
            value={form.slug}
            onChange={e => handleSlugChange(e.target.value)}
            placeholder="tax-sales"
          />
          <Form.Text className="text-muted">
            Lowercase letters, numbers, hyphens. Used in URLs.
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="text-muted text-uppercase" style={labelStyle}>
            Parent Category
          </Form.Label>
          <Form.Select
            value={form.parentId}
            onChange={e => setForm(f => ({ ...f, parentId: e.target.value }))}
            aria-label="Parent category"
          >
            <option value="">None (top level)</option>
            {parentOptions.map(c => (
              <option key={c.category_id} value={c.category_id}>{indentedLabel(c)}</option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            Nest this category under another one. Any depth is allowed.
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="text-muted text-uppercase d-block" style={labelStyle}>
            Icon
          </Form.Label>
          <div className="d-flex align-items-center gap-2 mb-2">
            <Button
              type="button"
              variant="outline-secondary"
              onClick={() => setPickerOpen(o => !o)}
              style={{ fontSize: '22px', width: '52px', height: '44px', padding: 0, lineHeight: 1 }}
              aria-label="Open emoji picker"
              aria-expanded={pickerOpen}
            >
              {form.icon || '🙂'}
            </Button>
            <span className="text-muted" style={{ fontSize: '13px' }}>
              {form.icon ? 'Click to change' : 'Click to choose an icon'}
            </span>
            {form.icon && (
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={() => setForm(f => ({ ...f, icon: '' }))}
                style={{ fontSize: '12px' }}
              >
                Clear
              </Button>
            )}
          </div>
          {pickerOpen && (
            <div className="mb-2">
              <EmojiPicker
                onEmojiClick={(emojiData) => {
                  setForm(f => ({ ...f, icon: emojiData.emoji }));
                  setPickerOpen(false);
                }}
                width="100%"
                height={350}
                previewConfig={{ showPreview: false }}
                skinTonesDisabled
              />
            </div>
          )}
        </Form.Group>

        <Form.Group className="mb-3" style={{ maxWidth: '160px' }}>
          <Form.Label className="text-muted text-uppercase" style={labelStyle}>
            Sort Order
          </Form.Label>
          <Form.Control
            type="number"
            value={form.sortOrder}
            onChange={e => setForm(f => ({ ...f, sortOrder: e.target.value }))}
          />
        </Form.Group>

        <Form.Group className="mb-2">
          {isTopLevel ? (
            <>
              <VisibilityPicker
                mode="category"
                label="Who can view this category?"
                idPrefix="category-visibility"
                value={audience || EVERYONE}
                onChange={setAudience}
                disabled={audienceUnavailable}
              />
              <Form.Text className={audienceUnavailable ? 'text-danger d-block' : 'text-muted d-block'}>
                {audienceUnavailable
                  ? 'Audience settings could not be loaded for this category. Reload the page and try again; other fields can still be saved.'
                  : 'Everyone includes signed-out visitors. Otherwise, users with a role in a selected application, or holding a selected role, can see it. Sub-categories inherit this.'}
              </Form.Text>
            </>
          ) : (
            <>
              <Form.Label className="text-muted text-uppercase d-block" style={labelStyle}>
                Who can view this category?
              </Form.Label>
              <div className="text-muted" style={{ fontSize: '13px' }}>
                Inherits visibility from <strong>{parentName || 'its parent'}</strong>. Set the audience on the
                top-level category.
              </div>
              {nestingDropsAudience && (
                <Alert variant="warning" className="py-2 mt-2 mb-0" style={{ fontSize: '13px' }}>
                  This category is currently limited to <strong>{summarize(audience)}</strong>. Saving it under{' '}
                  <strong>{parentName || 'a parent'}</strong> removes that restriction; it will be visible to
                  whoever can see the parent.
                </Alert>
              )}
            </>
          )}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          style={{ background: '#e85d04', border: 'none', fontWeight: 700 }}
        >
          {saving ? 'Saving…' : isNew ? 'Create Category' : 'Save Changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CategoryEditorModal;
